import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type {
  Egzemplarz, Indeks, Lista, Rezerwacja, StatusRezerwacji, Zajecia,
} from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  Karta, Komunikat, Ladowanie, Plakietka, Pole, Przycisk, Pusto, Wybor, Zakladki,
} from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { data as fData, godzina, ilosc } from '../lib/format';

const STATUS: Record<StatusRezerwacji, { etykieta: string; ton: 'ok' | 'uwaga' | 'blad' | 'neutralny' }> = {
  PLANOWANA: { etykieta: 'planowana', ton: 'neutralny' },
  AKTYWNA: { etykieta: 'aktywna', ton: 'ok' },
  WYDANA: { etykieta: 'wydana', ton: 'ok' },
  ZAKONCZONA: { etykieta: 'zakończona', ton: 'neutralny' },
  ANULOWANA: { etykieta: 'anulowana', ton: 'blad' },
};

export function Rezerwacje() {
  const { maUprawnienie } = useAuth();
  const mozeRezerwowac = maUprawnienie('rezerwacje.zapis', 'rezerwacje.zapisWlasne');
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusRezerwacji | ''>('');
  const [formularz, setFormularz] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  const parametry = new URLSearchParams({
    ...(status ? { status } : {}),
    od: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    naStronie: '100',
  });

  const lista = useQuery({
    queryKey: ['rezerwacje', parametry.toString()],
    queryFn: () => api.get<Lista<Rezerwacja>>(`/rezerwacje?${parametry}`),
    refetchInterval: 30_000,
  });

  const anuluj = useMutation({
    mutationFn: (r: Rezerwacja) =>
      api.put(`/rezerwacje/${r.id}/status`, { status: 'ANULOWANA', version: r.version }),
    onSuccess: () => { setBlad(null); void qc.invalidateQueries({ queryKey: ['rezerwacje'] }); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się anulować.'),
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Rezerwacje</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Ten sam egzemplarz nie zostanie zarezerwowany dwa razy w nakładających się godzinach —
            pilnuje tego baza, nie interfejs.
          </p>
        </div>
        <PrzyciskExcel dzial="rezerwacje" parametry={parametry} className="ml-auto" />
        {mozeRezerwowac && (
          <Przycisk wariant="glowny" onClick={() => setFormularz(!formularz)}>
            {formularz ? 'Zwiń formularz' : 'Nowa rezerwacja'}
          </Przycisk>
        )}
      </div>

      <Zakladki
        wartosc={status} zmien={setStatus}
        opcje={[
          { wartosc: '', etykieta: 'Wszystkie' },
          { wartosc: 'PLANOWANA', etykieta: 'Planowane' },
          { wartosc: 'AKTYWNA', etykieta: 'Aktywne' },
          { wartosc: 'ZAKONCZONA', etykieta: 'Zakończone' },
          { wartosc: 'ANULOWANA', etykieta: 'Anulowane' },
        ]}
      />

      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      {formularz && <FormularzRezerwacji zamknij={() => setFormularz(false)} />}

      {lista.isLoading ? <Ladowanie /> : (
        <Karta>
          {lista.data && lista.data.pozycje.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[940px] text-sm">
                <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Termin</th>
                    <th className="px-5 py-3 font-medium">Co</th>
                    <th className="px-5 py-3 font-medium">Na co</th>
                    <th className="px-5 py-3 font-medium">Zarezerwował</th>
                    <th className="px-5 py-3 font-medium">Stan</th>
                    <th className="px-5 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamien-200">
                  {lista.data.pozycje.map((r) => (
                    <tr key={r.id}>
                      <td className="whitespace-nowrap px-5 py-3">
                        <span className="text-kamien-900">{fData(r.startsAt)}</span>
                        <span className="ml-2 text-kamien-500">{godzina(r.startsAt)}–{godzina(r.endsAt)}</span>
                        <span className="block text-xs text-kamien-400">
                          blokada {godzina(r.blockedFrom)}–{godzina(r.blockedTo)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {r.equipmentUnit ? (
                          <>
                            <span className="font-mono text-xs text-kamien-500">{r.equipmentUnit.nrCsm}</span>{' '}
                            <span className="text-kamien-900">{r.equipmentUnit.item.name}</span>
                          </>
                        ) : (
                          <>
                            <span className="font-mono text-xs text-kamien-500">{r.item?.code}</span>{' '}
                            <span className="text-kamien-900">{r.item?.name}</span>
                            <span className="ml-2 text-kamien-600">
                              {ilosc(r.quantity)} {r.item?.unit}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="px-5 py-3 text-kamien-700">
                        {r.class
                          ? <>
                              {r.class.course.name}
                              <span className="block text-xs text-kamien-400">
                                {r.class.groupName ? `${r.class.groupName} · ` : ''}{r.class.instructorName}
                              </span>
                            </>
                          : r.issuePurpose?.name ?? '—'}
                      </td>
                      <td className="px-5 py-3 text-xs text-kamien-500">{r.createdBy.fullName}</td>
                      <td className="px-5 py-3">
                        <Plakietka ton={STATUS[r.status].ton}>{STATUS[r.status].etykieta}</Plakietka>
                      </td>
                      <td className="px-5 py-3">
                        {mozeRezerwowac && ['PLANOWANA', 'AKTYWNA'].includes(r.status) && (
                          <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs"
                            onClick={() => anuluj.mutate(r)}>Anuluj</Przycisk>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Pusto
              tytul="Brak rezerwacji"
              opis="Rezerwacja blokuje sprzęt na termin zajęć, wliczając bufor na czyszczenie i ładowanie."
            />
          )}
        </Karta>
      )}
    </div>
  );
}

function FormularzRezerwacji({ zamknij }: { zamknij: () => void }) {
  const qc = useQueryClient();
  const [dane, setDane] = useState({
    kind: 'EQUIPMENT_UNIT' as Rezerwacja['kind'],
    itemId: '', equipmentUnitId: '', quantity: '1', classId: '',
    startsAt: '', endsAt: '', notes: '',
  });
  const [blad, setBlad] = useState<string | null>(null);
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [sukces, setSukces] = useState<string | null>(null);

  const zajecia = useLista({
    queryKey: ['zajecia', 'do-rezerwacji'],
    queryFn: () => api.get<Lista<Zajecia>>(
      `/zajecia?od=${new Date(new Date().setHours(0, 0, 0, 0)).toISOString()}&rosnaco=true&naStronie=100`,
    ),
  });
  const sprzet = useLista({
    queryKey: ['sprzet', 'do-rezerwacji'],
    queryFn: () => api.get<Lista<Egzemplarz>>('/sprzet?condition=SPRAWNY&naStronie=300'),
  });
  const kartoteka = useLista({
    queryKey: ['kartoteka', 'do-rezerwacji'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?tylkoAktywne=true&naStronie=500'),
  });

  /** Wybór zajęć wypełnia termin — rezerwacja prawie zawsze pokrywa się z zajęciami. */
  function wybierzZajecia(id: string) {
    const z = zajecia.pozycje.find((x) => String(x.id) === id);
    setDane((d) => ({
      ...d, classId: id,
      startsAt: z ? z.startsAt.slice(0, 16) : d.startsAt,
      endsAt: z ? z.endsAt.slice(0, 16) : d.endsAt,
    }));
  }

  const zapisz = useMutation({
    mutationFn: () => api.post<{ rezerwacje: Rezerwacja[]; bufor: number }>('/rezerwacje', {
      kind: dane.kind,
      itemId: dane.kind === 'EQUIPMENT_UNIT' ? null : dane.itemId || null,
      equipmentUnitId: dane.kind === 'EQUIPMENT_UNIT' ? dane.equipmentUnitId || null : null,
      quantity: Number(dane.quantity.replace(',', '.')) || 1,
      classId: dane.classId || null,
      startsAt: new Date(dane.startsAt).toISOString(),
      endsAt: new Date(dane.endsAt).toISOString(),
      notes: dane.notes,
    }),
    onSuccess: (odp) => {
      void qc.invalidateQueries({ queryKey: ['rezerwacje'] });
      setSukces(
        `Zarezerwowano ${odp.rezerwacje.length} ${odp.rezerwacje.length === 1 ? 'pozycję' : 'pozycje'}. ` +
        `Blokada obejmuje termin zajęć powiększony o ${odp.bufor} min bufora.`,
      );
      setBlad(null);
    },
    onError: (e) => {
      setSukces(null);
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        setBlad(e.message);
      }
    },
  });

  const materialy = kartoteka.pozycje.filter((i) => i.type === 'MATERIAL');
  const typySprzetu = kartoteka.pozycje.filter((i) => i.type === 'EQUIPMENT');

  return (
    <Karta tytul="Nowa rezerwacja">
      <form className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); zapisz.mutate(); }}>
        {blad && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
        {sukces && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="sukces">{sukces}</Komunikat></div>}

        <div className="sm:col-span-2 lg:col-span-3">
          <Wybor etykieta="Na zajęcia" value={dane.classId} blad={bledy.classId ?? zajecia.blad}
            onChange={(e) => wybierzZajecia(e.target.value)} required>
            <option value="">— wybierz zajęcia —</option>
            {zajecia.pozycje.map((z) => (
              <option key={z.id} value={z.id}>
                {fData(z.startsAt)} {godzina(z.startsAt)} · {z.course.name}
                {z.groupName ? ` · ${z.groupName}` : ''} · {z.instructorName}
              </option>
            ))}
          </Wybor>
        </div>

        <Wybor etykieta="Co rezerwujemy" value={dane.kind}
          onChange={(e) => setDane({ ...dane, kind: e.target.value as Rezerwacja['kind'], itemId: '', equipmentUnitId: '' })}>
          <option value="EQUIPMENT_UNIT">Konkretny egzemplarz</option>
          <option value="EQUIPMENT_TYPE">Sprzęt danego typu</option>
          <option value="MATERIAL">Materiał (ilość)</option>
        </Wybor>

        <div className="lg:col-span-2">
          {dane.kind === 'EQUIPMENT_UNIT' ? (
            <Wybor etykieta="Egzemplarz" value={dane.equipmentUnitId}
              blad={bledy.equipmentUnitId ?? sprzet.blad}
              onChange={(e) => setDane({ ...dane, equipmentUnitId: e.target.value })} required>
              <option value="">— wybierz —</option>
              {sprzet.pozycje.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nrCsm} — {e.item.name}{e.model ? ` (${e.model})` : ''}
                </option>
              ))}
            </Wybor>
          ) : (
            <Wybor etykieta={dane.kind === 'MATERIAL' ? 'Materiał' : 'Typ sprzętu'}
              value={dane.itemId} blad={bledy.itemId ?? kartoteka.blad}
              onChange={(e) => setDane({ ...dane, itemId: e.target.value })} required>
              <option value="">— wybierz —</option>
              {(dane.kind === 'MATERIAL' ? materialy : typySprzetu).map((m) => (
                <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
              ))}
            </Wybor>
          )}
        </div>

        {dane.kind !== 'EQUIPMENT_UNIT' && (
          <Pole etykieta="Ilość" value={dane.quantity} inputMode="decimal" blad={bledy.quantity}
            onChange={(e) => setDane({ ...dane, quantity: e.target.value })} />
        )}

        <Pole etykieta="Od" type="datetime-local" value={dane.startsAt} blad={bledy.startsAt}
          onChange={(e) => setDane({ ...dane, startsAt: e.target.value })} required />
        <Pole etykieta="Do" type="datetime-local" value={dane.endsAt} blad={bledy.endsAt}
          onChange={(e) => setDane({ ...dane, endsAt: e.target.value })} required />

        <div className="sm:col-span-2 lg:col-span-3">
          <Pole etykieta="Uwagi" value={dane.notes}
            onChange={(e) => setDane({ ...dane, notes: e.target.value })} />
        </div>

        <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
          <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>
            {zapisz.isPending ? 'Rezerwowanie...' : 'Zarezerwuj'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Zamknij</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
